/**
 * Thin fetch wrapper over the saas-api endpoints.
 * The base URL is the only thing that varies between dev/prod, so it is
 * passed in. All payloads are validated server-side via Zod; the SDK is
 * intentionally trusting.
 *
 * Settings caching
 * ----------------
 * /v1/settings is cached client-side in localStorage and revalidated
 * with an HTTP ETag. On each pageload:
 *   1. If we have a cached {etag, settings} for this site, send
 *      `If-None-Match: <etag>` with the request.
 *   2. 304 ⇒ reuse the cached settings (no JSON parse, no network body).
 *   3. 200 ⇒ replace the cache with the new {etag, settings}.
 *   4. Network/server error ⇒ fall back to the cached settings if any.
 *
 * The browser HTTP cache is intentionally bypassed (`cache: "no-store"`)
 * so we control the validator round-trip ourselves rather than relying on
 * the browser's opaque heuristics across cross-origin requests.
 */

import type { BannerSettings, ConsentChoices, WebstatEvent } from "./types.js";

const CACHE_PREFIX = "csteer:settings:";

interface CachedSettings {
  etag: string;
  settings: BannerSettings;
}

/**
 * Thrown when the API refuses to serve this site's settings to the current
 * domain (HTTP 403). The page hosting the snippet is not the site's declared
 * domain (nor its www / a subdomain). Callers must NOT render the banner.
 */
export class DomainNotAuthorizedError extends Error {
  constructor(public readonly siteId: string) {
    super("domain_not_authorized");
    this.name = "DomainNotAuthorizedError";
  }
}

export class ApiClient {
  constructor(
    private readonly baseUrl: string,
    private readonly siteId: string,
  ) {}

  async fetchSettings(): Promise<BannerSettings | null> {
    const cached = readCache(this.siteId);
    const headers: Record<string, string> = { Accept: "application/json" };
    if (cached?.etag) headers["If-None-Match"] = cached.etag;

    let res: Response;
    try {
      res = await fetch(`${this.baseUrl}/v1/settings/${encodeURIComponent(this.siteId)}`, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        headers,
      });
    } catch {
      // Network / CORS error — last-resort fallback to the cache if any.
      return cached?.settings ?? null;
    }

    // 403: this domain isn't authorized for the site. Hard-stop — drop any
    // stale cache (so later loads also get a clean block) and signal the
    // caller to hide the banner. Never fall back to the cache here.
    if (res.status === 403) {
      clearCache(this.siteId);
      throw new DomainNotAuthorizedError(this.siteId);
    }

    // 304 Not Modified: the cached copy is still good.
    if (res.status === 304 && cached) return cached.settings;

    if (!res.ok) {
      // 404, 5xx — prefer to render the banner with whatever we last had
      // rather than blanking the page.
      return cached?.settings ?? null;
    }

    const settings = (await res.json()) as BannerSettings;
    const etag = res.headers.get("ETag");
    if (etag) writeCache(this.siteId, { etag, settings });
    return settings;
  }

  /**
   * Resolve the visitor's geo zone for geo-targeting. Best-effort + no-store
   * (per-visitor, never cached). `inConsentZone` is computed server-side
   * (EU/EEA/UK or unknown → true). Returns null on any error.
   */
  async fetchGeo(): Promise<{ country: string | null; inConsentZone: boolean } | null> {
    try {
      const res = await fetch(`${this.baseUrl}/v1/geo/${encodeURIComponent(this.siteId)}`, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
        headers: { Accept: "application/json" },
      });
      if (!res.ok) return null;
      return (await res.json()) as { country: string | null; inConsentZone: boolean };
    } catch {
      return null;
    }
  }

  async submitConsent(params: {
    choices: ConsentChoices;
    consentVersion: string;
    bannerSettingsHash: string;
  }): Promise<boolean> {
    const res = await fetch(`${this.baseUrl}/v1/consent`, {
      method: "POST",
      credentials: "omit",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        publicId: this.siteId,
        choices: params.choices,
        consentVersion: params.consentVersion,
        bannerSettingsHash: params.bannerSettingsHash,
      }),
    });
    return res.ok;
  }

  async submitWebstat(event: WebstatEvent, isMobile: boolean, variant?: "A" | "B"): Promise<void> {
    // Fire-and-forget; we don't block UI on stats.
    try {
      await fetch(`${this.baseUrl}/v1/webstats`, {
        method: "POST",
        credentials: "omit",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ publicId: this.siteId, event, isMobile, variant }),
        keepalive: true,
      });
    } catch {
      /* swallow */
    }
  }
}

/**
 * Hash the banner settings object so the server can prove what the user saw.
 * SHA-256 of the canonical-JSON serialization (sorted keys).
 */
export async function hashSettings(settings: BannerSettings): Promise<string> {
  const canonical = JSON.stringify(sortKeys(settings as unknown as Record<string, unknown>));
  const data = new TextEncoder().encode(canonical);
  const buf = await crypto.subtle.digest("SHA-256", data);
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

function sortKeys(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortKeys);
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(value as Record<string, unknown>).sort()) {
      out[k] = sortKeys((value as Record<string, unknown>)[k]);
    }
    return out;
  }
  return value;
}

// ============================================================================
// localStorage helpers
// ============================================================================

function readCache(siteId: string): CachedSettings | null {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + siteId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CachedSettings>;
    if (typeof parsed.etag === "string" && parsed.settings && typeof parsed.settings === "object") {
      return parsed as CachedSettings;
    }
    return null;
  } catch {
    return null;
  }
}

function writeCache(siteId: string, entry: CachedSettings): void {
  try {
    localStorage.setItem(CACHE_PREFIX + siteId, JSON.stringify(entry));
  } catch {
    // Quota exceeded, private mode, etc. — caching is a best-effort optimization.
  }
}

function clearCache(siteId: string): void {
  try {
    localStorage.removeItem(CACHE_PREFIX + siteId);
  } catch {
    // ignore — private mode / storage disabled
  }
}
